import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api/axiosConfig";
import { toast } from "react-toastify";
import { useAuth } from "./AuthContext";

const VendorContext = createContext();

export const useVendors = () => useContext(VendorContext);

export const VendorProvider = ({ children }) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // 1. FETCH ALL VENDORS
  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/vendors');
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setVendors(data);
      return data;
    } catch (err) {
      console.error("Error fetching vendors:", err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { 
    if (!user || !user.id) {
      setVendors([]);
    }
  }, [user]);

  // 2. FETCH SINGLE VENDOR BY ID (Direct REST endpoint, safe from loops)
  const fetchVendorById = useCallback(async (id) => {
    if (!id) return null;
    try {
      const res = await api.get(`/vendors/${id}`);
      return res.data?.data || res.data;
    } catch (err) {
      console.error("Error fetching vendor by ID:", err);
      return null;
    }
  }, []);

  // Synchronous cache helper for backwards compatibility
  const getVendorById = useCallback((id) => {
    return vendors.find(v => String(v.id) === String(id));
  }, [vendors]);

  // 3. ADD VENDOR
  const addVendor = async (vendorData) => {
    try {
      const res = await api.post('/vendors', vendorData);
      const newVendor = res.data?.data || res.data;
      setVendors(prev => [newVendor, ...prev]);
      toast.success("Vendor Added");
      return newVendor;
    } catch (err) {
      toast.error("Failed to add vendor");
      throw err;
    }
  };

  // 4. UPDATE VENDOR
  const updateVendor = async (id, vendorData) => {
    try {
      const res = await api.put(`/vendors/${id}`, vendorData);
      setVendors(prev => prev.map(v => String(v.id) === String(id) ? { ...v, ...vendorData } : v));
      toast.success("Vendor Updated");
      return res.data;
    } catch (err) {
      toast.error("Failed to update vendor");
      throw err;
    }
  };

  // 5. DELETE VENDOR
  const deleteVendor = async (id) => {
    try {
      await api.delete(`/vendors/${id}`);
      setVendors(prev => prev.filter(v => String(v.id) !== String(id)));
      toast.success("Vendor Deleted");
      return true;
    } catch (err) {
      toast.error("Failed to delete vendor");
      throw err;
    }
  };

  return (
    <VendorContext.Provider value={{ 
      vendors, 
      loading,
      addVendor, 
      updateVendor,
      deleteVendor,
      getVendorById, 
      fetchVendorById, 
      fetchVendors 
    }}>
      {children}
    </VendorContext.Provider>
  );
};